import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import FixedMenu from '../../../Common/Menus/FixedMenu.jsx';
import GoogleFontPicker from './GoogleFontPicker.jsx';
import {
  applyPortfolioFontTheme,
  getPortfolioFontTheme,
  googleFontStack,
} from '../../utilities/googleFonts.js';

const FontMenu = ({
  expanded,
  setExpanded,
  editorDocument,
  setEditorDocument,
  onPreviewFonts,
}) => {
  const theme = useMemo(() => getPortfolioFontTheme(editorDocument), [editorDocument]);
  const [draftTheme, setDraftTheme] = useState(theme);

  useEffect(() => {
    if (expanded) setDraftTheme(theme);
  }, [expanded, theme]);

  useEffect(() => {
    onPreviewFonts?.([draftTheme.body, draftTheme.headings].filter(Boolean));
    return () => onPreviewFonts?.([]);
  }, [draftTheme, onPreviewFonts]);

  const updateTheme = (key, value) => {
    const family = value ? value.split(',')[0].replace(/["']/g, '').trim() : '';
    setDraftTheme((current) => ({ ...current, [key]: family }));
  };

  const applyTheme = () => {
    setEditorDocument((previous) => applyPortfolioFontTheme(previous, draftTheme));
    setExpanded(false);
  };

  return (
    <FixedMenu
      className="new-blog-fixed-menu"
      animate
      title="Portfolio Fonts"
      iconClass="bi-fonts"
      expanded={expanded}
      setExpanded={setExpanded}
    >
      <div className="font-menu-intro mb-3">
        <h5 className="mb-1">Choose your typography</h5>
        <p className="text-muted small mb-0">Use one readable font for the page and an optional second font for headings.</p>
      </div>
      <div className="font-menu-grid">
        <GoogleFontPicker
          id="portfolio-body-font"
          label="Page font"
          value={googleFontStack(draftTheme.body)}
          onChange={(value) => updateTheme('body', value)}
          onPreview={(family) => onPreviewFonts?.([family, draftTheme.headings].filter(Boolean))}
          allowUnsupported={false}
        />
        <GoogleFontPicker
          id="portfolio-heading-font"
          label="Heading font"
          value={googleFontStack(draftTheme.headings)}
          onChange={(value) => updateTheme('headings', value)}
          onPreview={(family) => onPreviewFonts?.([draftTheme.body, family].filter(Boolean))}
          allowUnsupported={false}
        />
      </div>
      <div className="font-theme-preview mt-3" style={{ fontFamily: googleFontStack(draftTheme.body) || undefined }}>
        <h3 style={{ fontFamily: googleFontStack(draftTheme.headings) || 'inherit' }}>Your portfolio heading</h3>
        <p className="mb-0">A clear type pairing helps employers scan your experience, projects, and skills.</p>
      </div>
      <div className="d-flex justify-content-end gap-2 mt-3">
        <button className="btn btn-outline-secondary" type="button" onClick={() => setExpanded(false)}>Cancel</button>
        <button className="btn btn-primary" type="button" onClick={applyTheme}>Apply fonts</button>
      </div>
    </FixedMenu>
  );
};

FontMenu.propTypes = {
  expanded: PropTypes.bool.isRequired,
  setExpanded: PropTypes.func.isRequired,
  editorDocument: PropTypes.object,
  setEditorDocument: PropTypes.func.isRequired,
  onPreviewFonts: PropTypes.func,
};

export default FontMenu;
